import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  PlaceSchema,
  PlaceCreateInputSchema,
  PlaceUpdateInputSchema,
  type Place,
  type PlaceCreateInput,
  type PlaceUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `place` (lieu physique identifié, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`PlaceCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit jamais
 * un document pour découvrir ensuite qu'il est invalide. Les champs standard
 * sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { PlaceCreateInput };

/** Nom de la collection Firestore de la famille place. */
export const PLACES_COLLECTION = 'places';

/**
 * Repository typé pour la famille `place`.
 * Toute lecture est validée par `PlaceSchema`, toute écriture par
 * `PlaceCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface PlaceRepository {
  create(input: PlaceCreateInput): Promise<Place>;
  update(id: string, patch: PlaceUpdateInput): Promise<Place>;
  list(options?: ListOptions): Promise<Place[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Place | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Place>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Place>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Place>;
}

const placeConverter = createConverter<Place>((raw: DocumentData) => PlaceSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createPlaceRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): PlaceRepository {
  const col = collection(db, PLACES_COLLECTION).withConverter(placeConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Place>(db, col, PLACES_COLLECTION, getActorId);

  return {
    async create(input: PlaceCreateInput): Promise<Place> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = PlaceCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.address !== undefined) optional['address'] = fields.address;
      if (fields.parentPlaceId !== undefined) optional['parentPlaceId'] = fields.parentPlaceId;
      if (fields.isInternal !== undefined) optional['isInternal'] = fields.isInternal;
      if (fields.geo !== undefined) optional['geo'] = fields.geo;
      if (fields.ownerContactId !== undefined) optional['ownerContactId'] = fields.ownerContactId;
      if (fields.managingAgreementId !== undefined) optional['managingAgreementId'] = fields.managingAgreementId;
      if (fields.assetId !== undefined) optional['assetId'] = fields.assetId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Place);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return PlaceSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: PlaceUpdateInput): Promise<Place> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = PlaceUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, PLACES_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec PlaceSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`place ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Place[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Place | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
